// authServices.ts
import axiosClient from './axios-client'; // Import the axiosClient with interceptors
import { API_CONFIG } from '../config/api';
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";

// Function to handle user signup


// Function to handle user login


// Function to fetch user details

interface VerifyResponse {
  statusCode: number;
  message: string;
  statusId: number;
  statusName: string;
}

export const VerifyAccountApi = async (email: string): Promise<VerifyResponse> => {
  try {
    const response:VerifyResponse = await axiosClient.get("/SignUp/VerifyAccount", {
      params: { Email: email },
      validateStatus: (status) => true,
    });

    return response;
  } catch (error: any) {
    console.error("Error verifying account:", error);
    throw error; // Throwing the full error object for better error handling
  }
};

export const CreateAccountApi = async () => {
  try {
    const response = await axiosClient.post("/User/CreateAccount");
    return response;
  } catch (error: any) {
    console.error("Error creating account:", error);
    throw new Error(error.response?.data?.message || "Error creating account");
  }
};

export interface UploadFileResponse {
  message: string;
  fileUrl: string;
  userId: number;
}

export const uploadFileApi = async (file: File): Promise<UploadFileResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosClient.post('/File/FileUpload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Pushing up to 5MB over a slow uplink routinely outlives the 30s default
      // and surfaces as "Request timed out" on the signup proof step.
      timeout: API_CONFIG.UPLOAD_TIMEOUT,
    });
    // Access response.data (assuming interceptor returns full response object)
    const data = response.data || response; // Fallback to response if interceptor transforms it
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid upload response');
    }
    return data as UploadFileResponse;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export interface VerifyUploadResponse {
  message: string; 
  aiResponse: {
    task_id: string;
    status: string;
  };
}

export const verifyUploadApi = async (imageUrl: string, userId: number): Promise<VerifyUploadResponse> => {
  try {
    const response = await axiosClient.post('/File/VerifyUpload', {
      imageUrl,
      userId
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      // Kicks off the AI check; how long it holds the connection is the
      // backend's call, so it gets the upload budget rather than the default.
      timeout: API_CONFIG.UPLOAD_TIMEOUT,
    });
    // Access response.data (assuming interceptor returns full response object)
    const data = response.data || response; // Fallback to response if interceptor transforms it
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid verify response');
    }
    return data as VerifyUploadResponse;
  } catch (error) {
    console.error('Error verifying upload:', error);
    throw error;
  }
}

export const fetchAirlinesApi = async (query: string) => {
  try {
    const response = await axiosClient.get("/User/airlines", {
      params: { search: query },
      headers: { "Content-Type": "application/json" },
    });
    
    return response; // Returns an array of airlines
  } catch (error) {
    console.error("Error fetching airlines:", error);
    throw error;
  }
};

export const fetchHomeBasesApi = async (query: string) => {
  try {
    const response = await axiosClient.get("/User/airports", {
      params: { search: query },
      headers: { "Content-Type": "application/json" },
    });
    return response; // Array of home bases
  } catch (error) {
    console.error("Error fetching home bases:", error);
    throw error;
  }
};

export const updateAccountApi = async (
  firstName: string,
  lastName: string,
  employerAirlineId: number,
  homebaseId: number,
  referralCode: string
): Promise<void> => {
  try {
    await axiosClient.post('/User/UpdateAccount', {
      firstName,
      lastName,
      employerAirlineId,
      homebaseId,
      referralCode,
    });
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

export const validateReferralCodeApi = async (referralCode: string): Promise<void> => {
  try {
    await axiosClient.get('/Referrals/ValidateReferralCode', {
      params: { referralCode },
    });
  } catch (error) {
    console.error('Error validating referral code:', error);
    throw error;
  }
};

export const applyReferralApi = async (
  referralCode: string,
): Promise<{ message?: string }> => {
  try {
    const response = (await axiosClient.post('/User/ApplyReferral', {
      referralCode,
    })) as unknown as { message?: string };
    return response ?? {};
  } catch (error: any) {
    console.error('Error applying referral:', error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      'Failed to apply referral code.';
    throw new Error(message);
  }
};

export const checkEmailVerification = (email: string, tempPassword: string, callback: () => void) => {
  const auth = getAuth();

  // Stop polling after 5 minutes (100 attempts at 3s) so we never loop forever
  // — protects against forgotten cleanups and Firebase rate-limiting on bad creds.
  const MAX_ATTEMPTS = 100;
  let attempts = 0;

  const interval = setInterval(async () => {
    attempts += 1;
    if (attempts > MAX_ATTEMPTS) {
      clearInterval(interval);
      return;
    }

    try {
      if (!email) return;

      // Try signing in to refresh the user status
      await signInWithEmailAndPassword(auth, email, tempPassword);
      await auth.currentUser?.reload();

      if (auth.currentUser?.emailVerified) {
        clearInterval(interval); // Stop polling
        callback(); // Execute callback when email is verified
      }
    } catch (error) {
      console.error("Error checking email verification:", error);
    }
  }, 3000); // Check every 3 seconds

  return () => clearInterval(interval); // Return cleanup function
};
